<template>
  <v-card class="elevation-0 transparent">
    <v-card-text class="ma-0 pa-0">
      <v-tabs v-model="tab" bg-color="transparent" color="primary">
        <v-tab
          v-for="(creative, index) in modelValue.creatives"
          :key="creative.uuid"
          :text="`Variant ${index + 1}`"
          :value="index"
        >
          <template v-slot:append>
            <v-btn
              @click.prevent.stop="removeCreative(index)"
              variant="text"
              icon="mdi-close"
              color="error"
              density="compact"
            >
            </v-btn>
          </template>
        </v-tab>

        <v-btn
          color="primary"
          prepend-icon="mdi-plus"
          variant="text"
          class="ma-0 full-height-important"
          @click="addCreative"
          :disabled="modelValue.creatives.length >= 3"
        >
          Add Variant
        </v-btn>
      </v-tabs>

      <v-tabs-window v-model="tab">
        <v-tabs-window-item
          v-for="(creative, index) in modelValue.creatives"
          :key="creative.uuid"
          :value="index"
          eager
          class="pa-2"
        >
          <CampaignCreativeParameter
            v-for="param in parameters"
            :key="param.id"
            v-model="creative[param.key]"
            :param="param"
            :requirements="requirements[param.key]"
            :rules="getRules(param, index)"
            class="my-2"
          />
        </v-tabs-window-item>
      </v-tabs-window>
    </v-card-text>
  </v-card>
</template>


<script>
import CampaignCreativeParameter from "@/components/molecules/campaign/CampaignCreativeParameter.vue";
import { v4 as uuidv4 } from "uuid";

export default {
  name: "CampaignCreativeEditor",
  components: {
    CampaignCreativeParameter,
  },
  props: {
    modelValue: {
      type: Array,
      default: () => [{}],
    },
  },
  emits: ["update:modelValue"],

  data() {
    return {
      tab: 0,
      requiredParams: [],

      parameters: [],
      requirements: {},
      rules: {},
    };
  },
  computed: {},
  watch: {
    modelValue: {
      handler() {
        this.prepareParameters();
        this.prepareRequirements();
        if (!this.modelValue.creatives.length) {
          this.prepareFirstCreative();
        }
      },
      deep: true,
    },
  },
  mounted() {
    this.prepareParameters();
    this.prepareRequirements();
    this.prepareFirstCreative();
  },

  methods: {
    addCreative() {
      this.modelValue.creatives.push({
        uuid: uuidv4(),
        assets:JSON.parse(JSON.stringify(this.parameters)),
      });
    },
    removeCreative(index) {
      this.modelValue.creatives.splice(index, 1);
      this.tab = Math.max(0, this.tab - 1);
    },
    prepareFirstCreative() {
      if (!this.modelValue.creatives.length) {
        this.addCreative();
      }
    },
    prepareParameters() {
      this.parameters = [
        ...this.modelValue.type.Parameters.map((p) => {
          if (p.required) this.requiredParams.push(p.Parameter);

          return p.Parameter;
        }),

        ...(this.modelValue.targeting?.channels || []).reduce(
          (acc, channel) => {
            const channelParams = channel.Parameters.map((p) => {
              if (p.required) this.requiredParams.push(p.Parameter);

              return p.Parameter;
            });

            return [...acc, ...channelParams];
          },
          []
        ),
        ...(this.modelValue.targeting?.formats || []).reduce((acc, format) => {
          const formatParams = format.Parameters.map((p) => {
            if (p.required) this.requiredParams.push(p.Parameter);

            return p.Parameter;
          });

          return [...acc, ...formatParams];
        }, []),
      ]
        //   and remove duplicates
        .filter((param, index, self) => {
          return index === self.findIndex((p) => p.id === param.id);
        });
    },
    prepareRequirements() {
      const allRequirements = this.getRequirements();

      this.requirements = this.parameters.reduce((acc, param) => {
        const requirements = allRequirements.filter(
          (req) => req.parameter_id === param.id
        );

        acc[param.key] = requirements;
        return acc;
      }, []);

      console.log("requirements", this.requirements);
    },
    getRequirements() {
      return (
        [
          ...this.modelValue.type.Requirements,

          ...(this.modelValue.targeting?.channels || []).reduce(
            (acc, channel) => {
              const channelRequirements = channel.Requirements;

              return [...acc, ...channelRequirements];
            },
            []
          ),
          ...(this.modelValue.targeting?.formats || []).reduce(
            (acc, format) => {
              const formatRequirements = format.Requirements;

              return [...acc, ...formatRequirements];
            },
            []
          ),
        ]
          //   and remove duplicates
          .filter((param, index, self) => {
            return index === self.findIndex((p) => p.id === param.id);
          })
      );
    },
    getRules(param, index) {
      const isParamRequired = this.requiredParams.find(
        (p) => p.id === param.id
      );

      const alternativeParameters = this.parameters.filter(
        (p) => p.parameter_group === param.parameter_group && p.id !== param.id
      );

      //    just an [[],[]] with groups data inside by requirement_group property
      const requirementsGroups = this.requirements[param.key].reduce(
        (acc, requirement) => {
          const group = requirement.requirement_group;
          if (!group) return [...acc, [requirement]];

          if (acc.some((g) => g[0].requirement_group === group)) return acc;

          const groupRequirements = this.requirements[param.key].filter(
            (r) => r.requirement_group === group
          );

          console.log("groupRequirements", groupRequirements);

          return [...acc, groupRequirements];
        },
        []
      );

      console.log("requirementsGroups", requirementsGroups);
      console.log("alternativeParameters", alternativeParameters);

      const outRules = [
        ...(isParamRequired
          ? [
              (v) =>
                !!(param.type === "file" ? v[0] : v) ||
                alternativeParameters.some((p) => {
                  console.log(
                    "alternativeParameters",
                    p.key,
                    this.modelValue.creatives[index]
                  );
                  return !!this.modelValue.creatives[index][p.key];
                }) ||
                `${param.name} is required`,
            ]
          : []),
        ...(requirementsGroups.map((requirements) => {
          if (requirements.length > 1) {
            return (v) => {
              const isValid = requirements.some((req) =>
                this.getFormula(v, req)
              );

              if (isValid) return true;

              const invalidRequirements = requirements.filter(
                (req) => !this.getFormula(v, req)
              );

              console.log("invalidRequirements", invalidRequirements, v);

              return invalidRequirements
                .map((req) => req.description)
                .join(" OR ");
            };
          } else
            return (v) => {
              if (this.getFormula(v, requirements[0])) {
                return true;
              } else {
                return requirements[0].description
                  ? `${param.name} ${requirements[0].description}`
                  : `${param.name} is not valid`;
              }
            };
        }) || []),
      ];

      return outRules;
    },
    getFormula(v, requirement) {
      if (!v) return true;

      const value =
        requirement.property === "value" ? v : v[requirement.property];
      const target = requirement.value;

      switch (requirement.formula) {
        case "in":
          return target.split(";").includes(value);
        case "nin":
          return !target.split(";").includes(value);
        case "eq":
          return value === target;
        case "lt":
          return value < target;
        case "lte":
          return value <= target;
        case "gt":
          return value > target;
        case "gte":
          return value >= target;
        case "regex":
          return new RegExp(target).test(value);
        case "nregex":
          return !new RegExp(target).test(value);
      }
    },

    onUpdate() {
      this.$emit("update:modelValue", this.modelValue);
    },
  },
};
</script>